import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ListFilter } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { TASK_FILTER_OPTIONS, taskFilterOption } from "../mailTaskStatus";
import type { MailTaskFilter } from "../types";

type MailTaskFilterMenuProps = {
  value: MailTaskFilter;
  onChange: (value: MailTaskFilter) => void;
};

/** Narrows the list to emails still to deal with, overdue, done, or never given a task. */
export const MailTaskFilterMenu = ({ value, onChange }: MailTaskFilterMenuProps) => {
  const isActive = value !== "all";
  const label = isActive ? `Task filter: ${taskFilterOption(value).label}` : "Filter by task";

  return (
    <DropdownMenu>
      <div className="shrink-0 rounded-md bg-gray-100 p-1">
        <DropdownMenuTrigger
          aria-label={label}
          title={label}
          className={cn(
            "relative flex size-7 items-center justify-center rounded-md text-gray-700 transition-colors",
            "hover:bg-kPurple-50 hover:text-kPurple-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "data-[state=open]:bg-kPurple-50 data-[state=open]:text-kPurple-500",
            isActive && "bg-kPurple-500 text-white shadow-sm hover:bg-kPurple-500 hover:text-white",
          )}
        >
          <ListFilter className="size-4" />
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuLabel className="text-xs font-semibold text-gray-500">Show emails by task</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={value} onValueChange={next => onChange(next as MailTaskFilter)}>
          {TASK_FILTER_OPTIONS.map(({ value: option, label: optionLabel, icon: Icon }) => (
            <DropdownMenuPrimitive.RadioItem
              key={option}
              value={option}
              className={cn(
                "flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                "focus:bg-accent focus:text-accent-foreground data-[state=checked]:font-semibold",
              )}
            >
              <Icon className="size-4 shrink-0 text-gray-500" aria-hidden />
              <span className="flex-1">{optionLabel}</span>
              <DropdownMenuPrimitive.ItemIndicator>
                <Check className="size-4 text-kPurple-500" aria-hidden />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
